
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { PlanInterface, PlanRepositoryInterface } from '@/services/Plan/interfaces';
import { DataTableHeader } from 'vuetify';
import CreatePlanDialog from '@/components/dialogs/plan/CreatePlanDialog.vue';
import EditPlanDialog from '@/components/dialogs/plan/EditPlanDialog.vue';
import DeletePlanDialog from '@/components/dialogs/plan/DeletePlanDialog.vue';
import VComponent from '@/components/base/VComponent.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [Themeable],
  inject: [
    'planRepository',
  ],
  components: {
    PageHeader,
    VComponent,
    CreatePlanDialog,
    EditPlanDialog,
    DeletePlanDialog,
  },
})
export default class ListPlans extends Vue {
  public loading: boolean = true;

  private planRepository!: PlanRepositoryInterface;

  public headers: DataTableHeader[] = [
    { text: 'Id', value: 'id' },
    { text: 'Name', value: 'name' },
    { text: 'Quota', value: 'quota' },
    { text: 'Bandwidth', value: 'bandwidth' },
    { text: 'Websites Count', value: 'websitesCount' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public plans: PlanInterface[] = [];
  public options: ListPaginationOptions = defaultListOptions();
  public readonly LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public createPlanDialog: string | null = null;

  public updatePlanDialog: string | null = null;
  public planForUpdateDialog!: PlanInterface;

  public deletePlanDialog: string | null = null;
  public planForDeleteDialog!: PlanInterface;

  public async created(): Promise<void> {
    this.applyRouteQuery();
    try {
      this.plans = await this.planRepository.getAll();
      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const parsed = parseListQuery(this.$route.query);
      this.options.page = parsed.page;
      this.options.itemsPerPage = parsed.itemsPerPage;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({
        name: 'all_plans',
        query: buildListPaginationQuery(this.options),
      });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  @Watch('options.page')
  @Watch('options.itemsPerPage')
  public async onPaginationChange(): Promise<void> {
    await this.syncRouteQuery();
  }

  @Watch('$route')
  public onRouteChange(): void {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'all_plans') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }
    this.applyRouteQuery();
  }

  public showCreateDialog(): void {
    this.createPlanDialog = 'create-plan-dialog';
  }

  public async createPlan(name: string, quota: number, bandwidth: number, websiteCount: number): Promise<void> {
    try {
      const plan = await this.planRepository.create({
        name, quota, bandwidth, websiteCount
      });

      this.plans.push(plan);

      this.$successDialog.show(`Plan ${name} created successfully.`);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }

  public showEditDialog(plan: PlanInterface): void {
    this.planForUpdateDialog = plan;
    this.updatePlanDialog = 'edit-plan-dialog';
  }

  public async editPlan(id: number, name: string, quota: number, bandwidth: number, websitesCount: number): Promise<void> {
    try {
      const plan = await this.planRepository.update(id, {
        name, quota, bandwidth, websitesCount
      });
      const index = this.plans.findIndex((p) => p.id === id);
      if (!plan) {
        return;
      }

      this.plans[index].name = name;

      this.$successDialog.show(`Plan updated successfully.`);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }

  public showDeleteDialog(plan: PlanInterface): void {
    this.planForDeleteDialog = plan;
    this.deletePlanDialog = 'delete-plan-dialog';
  }

  public async deletePlan(id: number): Promise<void> {
    try {
      await this.planRepository.delete(id);

      this.plans = this.plans.filter((p) => p.id !== id);

      this.$successDialog.show(`Plan deleted successfully.`);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }
}
